import { useEffect, useMemo, useState } from "react"
import { useApolloClient } from "@apollo/client"
import query from "@/v2-backoffice-client/license/seats/license.seats.holders.query.gql"
import type {
  LicenseSeatHoldersQuery,
  LicenseSeatHoldersQueryVariables,
} from "@/v2-backoffice-client/license/seats/__types__/license.seats.holders.query"

type SeatHolder = LicenseSeatHoldersQuery["getLicenseSeatHolders"][number]

/**
 * The seat holders of several customers at once, for a list of users whose
 * owning customers differ (a team's members: nearly always one customer,
 * now and then a second one's person on it). One watched read per
 * customer, the same cache entry useLicenseSeatHoldersQuery fills, so a
 * grant or revoke that evicts it refreshes this list too. A read that
 * fails leaves its customer out; the column just stays blank for them.
 */
export function useLicenseSeatHoldersByCustomerIds(
  customerIds: readonly (number | null | undefined)[],
): SeatHolder[] {
  const client = useApolloClient()
  // The distinct ids as one string, so the reads re-subscribe when the set
  // changes and not when the same ids arrive in another order.
  const key = [...new Set(customerIds.filter((id) => id != null))]
    .toSorted((left, right) => left - right)
    .join(",")
  const ids = useMemo(() => (key ? key.split(",").map(Number) : []), [key])
  const [holdersByCustomer, setHoldersByCustomer] = useState<
    Map<number, SeatHolder[]>
  >(new Map())

  useEffect(() => {
    const subscriptions = ids.map((customerId) =>
      client
        .watchQuery<LicenseSeatHoldersQuery, LicenseSeatHoldersQueryVariables>({
          query,
          variables: { customerId },
          fetchPolicy: "cache-first",
          errorPolicy: "all",
        })
        .subscribe({
          next(result) {
            const holders = result.data?.getLicenseSeatHolders
            if (!holders) return
            setHoldersByCustomer((previous) =>
              new Map(previous).set(customerId, holders),
            )
          },
          error() {},
        }),
    )
    return () => {
      for (const subscription of subscriptions) subscription.unsubscribe()
    }
  }, [client, ids])

  // Read for the current set only; what an earlier set left in the map is
  // ignored, not cleared, so a set change needs no extra render.
  return useMemo(
    () => ids.flatMap((customerId) => holdersByCustomer.get(customerId) ?? []),
    [ids, holdersByCustomer],
  )
}
